/**
 * The frame every screen hangs in: rail beside the content on a wide screen,
 * header above and tab bar below on a phone.
 *
 * It owns exactly one decision — which of the two frames to draw — and it takes
 * that decision from `media.ts`'s single breakpoint, so the CSS and the
 * component can never disagree about what "narrow" means. Everything else
 * (what the summary strip says, which pane is open, what the tab bar switches
 * between) belongs to the screen inside it.
 */
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { useNarrow, useViewportHeightVar } from '../media';
import { SessionExpiredPrompt } from '../components/Account';
import { Brand } from './Brand';
import { PageTabBar } from './TabBar';
import type { NavKey } from './Rail';
import { Rail } from './Rail';

export function AppShell({
  active,
  children,
  top,
  tabBar,
  header,
  fill = false,
}: {
  active?: NavKey | undefined;
  children: ReactNode;
  /** summary strip and banners — the workspace's, above map and inspector both */
  top?: ReactNode;
  /** the phone's bottom tab bar; the page one is used when a screen has none */
  tabBar?: ReactNode;
  /** the phone's 48 px header; a brand line is used when a screen has none */
  header?: ReactNode;
  /** the content fills its box and scrolls inside itself (the workspace) */
  fill?: boolean;
}) {
  const narrow = useNarrow();
  useViewportHeightVar();

  if (narrow) {
    return (
      <div className="ae-shell phone">
        <div className="ae-phonetop">
          {header ?? <PhoneHeader />}
          {top}
        </div>
        <div className={'ae-content' + (fill ? ' fill' : '')}>{children}</div>
        {tabBar ?? <PageTabBar active={active} />}
        <SessionExpiredPrompt />
      </div>
    );
  }

  return (
    <div className="ae-shell">
      <Rail active={active} />
      <div className="ae-main">
        {/* one element, always: `.ae-main` is a two-row grid, and `top` is a
            fragment of two children (strip + banners). Letting them into the
            grid directly gave it three items, so the `1fr` row landed on the
            banners and the content fell into an implicit row — a 437 px hole
            above the map with banners up, and a banner drawn under the map
            without. The phone branch has always wrapped; this matches it. */}
        <div className="ae-top">{top}</div>
        <div className={'ae-content' + (fill ? ' fill' : '')}>{children}</div>
      </div>
      <SessionExpiredPrompt />
    </div>
  );
}

/** The default phone header: the mark, the word, and nothing else. */
export function PhoneHeader() {
  const t = useT();
  return (
    <header className="ae-phonehead">
      <Link to="/" className="ae-brand" aria-label={t('nav.flights')}>
        <Brand size="sm" />
      </Link>
    </header>
  );
}
